import { useMemo, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { useGlossary } from '../glossary/GlossaryContext';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  GLOSSARY,
  searchTerms,
  type GlossaryCategory,
  type GlossaryTerm,
} from '../glossary/terms';

// ────────────────────────────────────────────────────────────────────────────
// Glossary page (#glossary).
//
// The global "all terms" view. Search bar at the top, then terms grouped by
// category and alphabetized within each. Clicking a term opens the panel
// pinned to it (same UI used inline throughout the app).
// ────────────────────────────────────────────────────────────────────────────

interface Props {
  onBack: () => void;
}

export function GlossaryPage({ onBack }: Props) {
  const { open } = useGlossary();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => searchTerms(query), [query]);

  const grouped = useMemo(() => {
    const map = new Map<GlossaryCategory, GlossaryTerm[]>();
    for (const t of filtered) {
      const list = map.get(t.category) ?? [];
      list.push(t);
      map.set(t.category, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.term.localeCompare(b.term));
    }
    return map;
  }, [filtered]);

  return (
    <>
      <TopBar
        title="Glossary"
        subtitle={`${GLOSSARY.length} terms · tax, retirement & Cairn-specific`}
        showScenario={false}
        right={
          <button className="btn btn-ghost text-sm" onClick={onBack}>
            Back
          </button>
        }
      />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 pb-nav">
        <section className="card card-dark p-4">
          <label
            htmlFor="glossary-search"
            className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300"
          >
            Search
          </label>
          <input
            id="glossary-search"
            type="search"
            className="input mt-1"
            placeholder="Try “402(g)”, “IRMAA”, “mega backdoor”…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
          />
          <p className="mt-2 text-[11px] text-ink-500 dark:text-ink-300">
            Click any term to read the full explanation. Throughout the app, words
            with a dotted underline open this same glossary.
          </p>
        </section>

        {filtered.length === 0 ? (
          <section className="card card-dark p-6 text-center text-sm text-ink-500 dark:text-ink-300">
            No matches for <span className="font-semibold">"{query}"</span>. Try a
            broader query or browse the full list (clear the search box).
          </section>
        ) : (
          CATEGORY_ORDER.filter(c => (grouped.get(c)?.length ?? 0) > 0).map(cat => (
            <section key={cat} className="card card-dark overflow-hidden">
              <div className="border-b border-ink-100 bg-ink-50/60 px-4 py-2 dark:border-ink-700 dark:bg-ink-800/50">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
                  {CATEGORY_LABELS[cat]}
                </h2>
                <p className="mt-0.5 text-[11px] text-ink-400">
                  {grouped.get(cat)!.length}{' '}
                  {grouped.get(cat)!.length === 1 ? 'term' : 'terms'}
                </p>
              </div>
              <ul className="divide-y divide-ink-100 dark:divide-ink-700">
                {grouped.get(cat)!.map(t => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => open(t.id)}
                      className="block w-full px-4 py-3 text-left transition hover:bg-ink-50 focus-visible:bg-ink-50 focus-visible:outline-none dark:hover:bg-ink-800/60 dark:focus-visible:bg-ink-800/60"
                    >
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-ink-900 dark:text-ink-50">
                          {t.term}
                        </span>
                        {t.currentValue && (
                          <span className="rounded bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium text-accent-700 dark:bg-accent-700/20 dark:text-accent-500">
                            {t.currentValue}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[12px] leading-relaxed text-ink-600 dark:text-ink-300">
                        {t.short}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}

        {/* Suggest-a-term affordance — placeholder. We deliberately don't
            wire a backend; document the intent in code so the next pass can
            decide whether this becomes a GitHub-issue link or a no-op. */}
        <section className="card card-dark p-4 text-center">
          <p className="text-xs text-ink-500 dark:text-ink-300">
            Missing a term? Glossary entries live in{' '}
            <code className="rounded bg-ink-100 px-1 py-0.5 text-[11px] dark:bg-ink-800">
              src/glossary/terms.ts
            </code>{' '}
            — open an issue or PR to suggest one.
          </p>
        </section>
      </main>
    </>
  );
}
